import React from "react";
import {
    Badge, Box, Flex, Icon, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
    useColorModeValue,
} from "@chakra-ui/react";
import { MdVerified, MdChevronRight } from "react-icons/md";
import dayjs from "dayjs";
import { getCertificationType, getStatusConfig } from "./certificationUtils";

/**
 * Table header row with column labels.
 * @returns {React.ReactElement}
 */
function TableHeader() {
    return (
        <Thead>
            <Tr>
                <Th>Nom</Th>
                <Th>Type</Th>
                <Th>Statut</Th>
                <Th></Th>
            </Tr>
        </Thead>
    );
}

/**
 * Loading state row with centered spinner.
 * @returns {React.ReactElement}
 */
function LoadingRow() {
    return (
        <Tr>
            <Td colSpan={4} textAlign="center" py="40px" border="none">
                <Spinner size="lg" color="brand.500" />
            </Td>
        </Tr>
    );
}

/**
 * Empty state row displayed when no certifications match the filter.
 * @returns {React.ReactElement}
 */
function EmptyRow() {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Tr>
            <Td colSpan={4} textAlign="center" py="40px" color={color} border="none">
                Aucune certification trouvée
            </Td>
        </Tr>
    );
}

/**
 * Name cell displaying certification name and modification date.
 * @param {Object} props
 * @param {Object} props.cert - Certification data object
 * @param {boolean} props.isSelected - Whether the parent row is selected
 * @returns {React.ReactElement}
 */
function NameCell({ cert, isSelected }) {
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    const dateColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Td>
            <Box display="flex" flexDirection="column" gap="2px">
                <Text fontSize="sm" fontWeight={isSelected ? "700" : "500"} color={nameColor}>
                    {cert.name || cert.artist_name || "Sans nom"}
                </Text>
                <Text fontSize="xs" color={dateColor}>
                    {dayjs(cert.modified_at).format("DD/MM/YYYY HH:mm")}
                </Text>
            </Box>
        </Td>
    );
}

/**
 * Colored badge with an icon, used for type/status cells.
 * @param {Object} props
 * @param {React.ElementType} props.icon - react-icons component
 * @param {string} props.label - Badge label
 * @param {string} props.color - Accent color (hex)
 * @returns {React.ReactElement}
 */
function IconBadge({ icon, label, color }) {
    return (
        <Badge bg={`${color}20`} color={color} border={`1px solid ${color}40`}
            borderRadius="full" px="8px" py="2px" textTransform="none">
            <Flex align="center" gap="4px">
                <Icon as={icon} w="14px" h="14px" color={color} />
                {label}
            </Flex>
        </Badge>
    );
}

/**
 * Single certification row with name, type badge, and status badge.
 * @param {Object} props
 * @param {Object} props.cert - Certification data object
 * @param {boolean} props.isSelected - Whether this row is currently selected
 * @param {Function} props.onSelect - Callback to select this certification
 * @returns {React.ReactElement}
 */
function CertificationRow({ cert, isSelected, onSelect }) {
    const selectedBg = useColorModeValue("brand.50", "whiteAlpha.100");
    const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.200");
    const chevronColor = useColorModeValue("secondaryGray.500", "secondaryGray.400");
    const statusConfig = getStatusConfig(cert.status);
    const typeConfig = getCertificationType(cert);
    return (
        <Tr onClick={() => onSelect(cert)} cursor="pointer" transition="all 0.2s ease"
            bg={isSelected ? selectedBg : "transparent"} _hover={{ bg: hoverBg }}
            borderLeft={isSelected ? "3px solid var(--chakra-colors-brand-500)" : "3px solid transparent"}>
            <NameCell cert={cert} isSelected={isSelected} />
            <Td><IconBadge icon={typeConfig.icon} label={typeConfig.label} color={typeConfig.color} /></Td>
            <Td><IconBadge icon={MdVerified} label={statusConfig.label} color={statusConfig.color} /></Td>
            <Td textAlign="right" w="1%"><Icon as={MdChevronRight} w="20px" h="20px" color={chevronColor} /></Td>
        </Tr>
    );
}

/**
 * Renders table body with loading, empty, or certification rows.
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Array} props.certifications - Filtered certifications list
 * @param {number|null} props.selectedId - Currently selected certification ID
 * @param {Function} props.onSelect - Row selection handler
 * @returns {React.ReactElement}
 */
function CertTableBody({ isLoading, certifications, selectedId, onSelect }) {
    if (isLoading) return <Tbody><LoadingRow /></Tbody>;
    if (certifications.length === 0) return <Tbody><EmptyRow /></Tbody>;
    return (
        <Tbody>
            {certifications.map((cert) => (
                <CertificationRow key={cert.id} cert={cert}
                    isSelected={selectedId === cert.id} onSelect={onSelect} />
            ))}
        </Tbody>
    );
}

/**
 * Table displaying filtered certifications with selection highlighting.
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Array} props.filteredCertifications - Filtered certification list
 * @param {Object|null} props.selectedCertification - Currently selected certification
 * @param {Function} props.onSelect - Callback to select a certification row
 * @returns {React.ReactElement}
 */
function CertificationTable({ isLoading, filteredCertifications, selectedCertification, onSelect }) {
    const border = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    return (
        <TableContainer maxH="560px" overflowY="auto" border="1px solid" borderColor={border} borderRadius="12px">
            <Table size="sm">
                <TableHeader />
                <CertTableBody isLoading={isLoading} certifications={filteredCertifications}
                    selectedId={selectedCertification?.id} onSelect={onSelect} />
            </Table>
        </TableContainer>
    );
}

export { CertificationTable };
